/**
 * The pre-game lobby: who is here, the code to share, and the host's START.
 *
 * DOM only; every decision it renders is made by the pure helpers in `lobby.ts`.
 * Same split as `RoundHud` and `TagHud` beside it.
 *
 * It sits over the live world rather than replacing it — you are already in the
 * level during Warmup and free to walk around, and a menu that hid the map
 * would be hiding the thing you are about to play. It is also the one piece of
 * HUD that takes pointer events, because it is the one piece with a button in
 * it: everything else in `hud/` is deliberately `pointer-events: none` so it
 * cannot eat a click meant for the canvas.
 */

import { lobbyRoster, lobbyView, type LobbyStats } from "./lobby.js";

export interface LobbyHudStats extends LobbyStats {
  localId: number;
  localName: string;
  peers: ReadonlyMap<number, string>;
  hostId: number;
  /** The lobby code, for the line people read out over voice. */
  code: string;
}

/** How long the copy button acknowledges a click. */
const COPIED_HOLD_MS = 1400;

export class LobbyHud {
  private readonly panel: HTMLElement;
  private readonly codeEl: HTMLElement;
  private readonly copyButton: HTMLButtonElement;
  private readonly rosterEl: HTMLElement;
  private readonly levelEl: HTMLElement;
  private readonly noteEl: HTMLElement;
  private readonly startButton: HTMLButtonElement;

  /** What the roster last rendered, so it is not rebuilt 60 times a second. */
  private rosterKey = "";
  private copiedUntil = 0;

  constructor(root: HTMLElement, onStart: () => void) {
    this.panel = document.createElement("div");
    this.panel.className = "lobby-panel";
    this.panel.hidden = true;

    const title = document.createElement("div");
    title.className = "lobby-title";
    title.textContent = "LOBBY";

    const codeRow = document.createElement("div");
    codeRow.className = "lobby-code-row";
    this.codeEl = document.createElement("span");
    this.codeEl.className = "lobby-code";
    this.copyButton = document.createElement("button");
    this.copyButton.type = "button";
    this.copyButton.className = "lobby-copy";
    this.copyButton.textContent = "copy invite";
    this.copyButton.addEventListener("click", () => {
      void this.copyInvite();
    });
    codeRow.append(this.codeEl, this.copyButton);

    this.rosterEl = document.createElement("ul");
    this.rosterEl.className = "lobby-roster";

    // Between the code and the roster: it belongs with the facts about *this
    // lobby* rather than with the note, which is about what happens next. Given
    // an id to make it findable from the e2e suite, in the same spirit as
    // #lobby-start — it is the one thing on this panel a level test can assert.
    this.levelEl = document.createElement("div");
    this.levelEl.id = "lobby-level";
    this.levelEl.className = "lobby-level";

    this.noteEl = document.createElement("div");
    this.noteEl.className = "lobby-note";

    this.startButton = document.createElement("button");
    this.startButton.type = "button";
    this.startButton.id = "lobby-start";
    this.startButton.className = "lobby-start";
    this.startButton.textContent = "START";
    this.startButton.addEventListener("click", onStart);

    this.panel.append(
      title, codeRow, this.levelEl, this.rosterEl, this.noteEl, this.startButton,
    );
    root.appendChild(this.panel);
  }

  update(stats: LobbyHudStats, now: number): void {
    const view = lobbyView(stats);
    this.panel.hidden = !view.visible;
    if (!view.visible) return;

    this.codeEl.textContent = stats.code;
    // Hidden rather than blank before the Welcome names it: an empty "level"
    // label reads as a level with no name, which is worse than no label.
    this.levelEl.hidden = view.levelName === "";
    this.levelEl.textContent = `level: ${view.levelName}`;
    this.startButton.hidden = !view.showStart;
    this.startButton.disabled = !view.startEnabled;
    this.noteEl.textContent = view.note;

    const members = lobbyRoster(stats.localId, stats.localName, stats.peers, stats.hostId);
    // Rebuilding the list every frame would restart the CSS transition on every
    // row and make the roster flicker; comparing a key is cheaper than
    // diffing and this list is at most MAX_PLAYERS long.
    const key = members.map((m) => `${m.id}:${m.name}:${m.isHost ? 1 : 0}`).join("|");
    if (key !== this.rosterKey) {
      this.rosterKey = key;
      this.rosterEl.replaceChildren(
        ...members.map((m) => {
          const li = document.createElement("li");
          li.textContent = m.isYou ? `${m.name} (you)` : m.name;
          if (m.isHost) li.dataset.host = "true";
          return li;
        }),
      );
    }

    if (this.copiedUntil !== 0 && now > this.copiedUntil) {
      this.copiedUntil = 0;
      this.copyButton.textContent = "copy invite";
    }
  }

  /**
   * Put the invite on the clipboard.
   *
   * `location.href` rather than a URL built here: the lobby code is already in
   * the hash (main.ts puts it there on join), so the address bar *is* the
   * invite — which is the property the join screen's hint has always claimed.
   *
   * The clipboard API rejects without a secure context or a user gesture. This
   * runs from a click so the gesture is there, but http:// on a LAN address is
   * a real way to play this, so a failure falls back to selecting the text for
   * the player to copy by hand rather than silently doing nothing.
   */
  private async copyInvite(): Promise<void> {
    try {
      await navigator.clipboard.writeText(location.href);
      this.copyButton.textContent = "copied";
      this.copiedUntil = performance.now() + COPIED_HOLD_MS;
    } catch {
      this.copyButton.textContent = "copy the URL";
      this.copiedUntil = performance.now() + COPIED_HOLD_MS;
    }
  }
}
